/**
 * CadenceStepSectionPure
 *
 * Pure presentational component for the cadence step editor.
 * Receives all data and callbacks via props, no server dependencies.
 *
 * Editor Render Props (all optional - defaults to built-in components):
 * - renderSubjectEditor: Override the subject editor (defaults to TipTapInput)
 * - renderBodyEditor: Override the body editor (defaults to TipTapInput)
 * - renderReminderInput: Override the reminder selector (defaults to ManualSelectPure)
 * - renderSelectDropdown: Override email dropdowns (From, To)
 * - renderSuggestTemplates: Template suggestions (optional)
 * - renderCharCounter: Character counter for connect messages (optional)
 *
 * When render props are not provided:
 * - TipTapInput is used for subject/body with @ variable and / document mentions
 * - ManualSelectPure is used for reminder selection (requires reminderActions prop)
 *
 * Reminder Data Props (used when renderReminderInput not provided):
 * - reminderActions: Available reminder actions
 * - isLoadingReminderActions: Loading state
 * - onCreateReminderAction: Callback to create new reminder
 * - isCreatingReminderAction: Creating state
 */
import type { ReactNode, RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import type { Editor } from '@tiptap/react';
import { cn } from '../cn';
import { Badge } from '../Badge';
import Input from '../Input2/Input';
import { General, Arrows } from '../StrokeIcons';
import { WaveSpinner } from '../WaveSpinner';
import Button from '../Input2/Button';
import ButtonGroup from '../Input2/ButtonGroup';
import ButtonSegment from '../Input2/ButtonSegment';
import FilterSelect from '../Input2/FilterSelect';
import TipTapInput, { type TipTapVariable, type TipTapDocument } from '../Input2/TipTapInput';
import { Tooltip } from '../Tooltip';
import { ESC_PRIORITY, useEscStack } from '../contexts';
import { UpdateStepTypePopup } from '../Popup';
import { StepIcon } from '../StepIcon';
import { ManualSelectPure, type ReminderAction } from '../ManualSelect';
import { MessageAttachmentButtonPure, type MessageAttachmentState } from './MessageAttachmentButtonPure';
import { UserAtVariables } from '@poseidon/types/types/atVariables.type';

const USER_AT_VARS_SET = new Set<string>(UserAtVariables);

// ============================================================================
// Types
// ============================================================================

export type CadenceStepType =
  | 'message'
  | 'connect'
  | 'email'
  | 'inmail'
  | 'follow'
  | 'visit'
  | 'likeRecentPosts'
  | 'reminder'
  | 'call'
  | 'voice_note'
  | 'sift';

export type SendMode = 'now' | 'scheduled';

export type StepChanges = {
  from?: string;
  to?: string;
  subject?: string;
  body2?: string;
  body?: string;
  cc?: string;
  bcc?: string;
  postsToLike?: number;
  onlyOpenLink?: boolean;
  useEmailSignature?: boolean;
  type?: CadenceStepType;
};

export type StepData = {
  id: string;
  type: CadenceStepType;
  subject: string | null;
  body2: string | null;
  from: string | null;
  to: string | null;
  cc: string | null;
  bcc: string | null;
  onlyOpenLink?: boolean;
  useEmailSignature?: boolean;
};

export type EditorDocument = {
  id: string;
  title?: string;
  type?: string;
  isPrivate?: boolean;
  imageUrl?: string;
};

export type EmailOption = {
  value: string;
  label: string;
  helperText?: string;
  primary?: boolean;
  lastUsed?: boolean;
};

export type ThreadOption = {
  label: string;
  value: string;
  helperText?: string;
};

export type AiChoice = {
  subject?: string;
  body: string;
};

export type StepListItem = {
  value: CadenceStepType;
  label: string;
  labelFull: ReactNode;
};

export type ContactForStep = {
  id: string;
  primaryFields?: {
    professionalEmail?: { data?: string | null } | null;
    phone?: { data?: string | null } | null;
  } | null;
};

export type ContactUserForStep = {
  liConnectedAt?: string | Date | null;
  emailCredentialId?: string | null;
};

export type UserTeamForStep = {
  teamId: string;
  userId: string;
  isPremium: boolean;
  useEmailSignatureByDefault?: boolean;
  useContactEmailAsBcc?: boolean;
  unipileAccountId?: string | null;
};

// ============================================================================
// Main Component Props
// ============================================================================

export type CadenceStepSectionPureProps = {
  // Core data
  userTeam: UserTeamForStep;
  contact: ContactForStep;
  contactUser: ContactUserForStep;
  step: StepData;
  atVariables: Record<string, string>;

  // Server data (passed from wrapper)
  documents?: EditorDocument[];
  fromEmailOptions?: EmailOption[];
  toEmailOptions?: EmailOption[];
  threadOptions?: ThreadOption[];

  // Reminder actions (for reminder step type)
  reminderActions?: ReminderAction[];
  isLoadingReminderActions?: boolean;
  onCreateReminderAction?: (name: string) => Promise<void>;
  isCreatingReminderAction?: boolean;

  // AI state
  aiChoices?: AiChoice[];
  aiIndex?: number;
  aiCommand?: string;
  aiLoading?: boolean;

  // Step list for header
  stepsList?: StepListItem[];

  // Config
  allowEditStepType?: boolean;
  allowSaveAsTemplate?: boolean;
  allowEmailDetails?: boolean;
  disableLikeRecentPosts?: boolean;
  autoGenerateAi?: boolean;
  initiallyAnimate?: boolean;

  // Send mode
  sendMode?: SendMode;
  showSendModeDropdown?: boolean;
  onSendModeChange?: (mode: SendMode) => void;

  // File attachment (message/inmail only). Omitted → no attach button rendered.
  messageAttachment?: MessageAttachmentState;

  // Texts
  texts?: {
    improve?: string;
    generate?: string;
    submit?: string;
  };

  // Loading states
  isSubmitting?: boolean;
  isAiGenerating?: boolean;
  isSavingSnippet?: boolean;

  // Callbacks
  onSubmit: (step: StepData) => Promise<void>;
  onCancel?: () => void;
  onChange: (changes: StepChanges) => void;
  onGenerateAi?: (empty?: boolean) => Promise<void>;
  onAiCommandChange?: (command: string) => void;
  onAiIndexChange?: (index: number) => void;
  onSaveSnippet?: () => Promise<void>;
  onLoadThreads?: () => Promise<void>;
  onToast?: (message: string, type: 'error' | 'success') => void;

  // Render props for platform-specific components
  renderSubjectEditor?: (props: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    documents: EditorDocument[];
    atVariables: Record<string, string>;
    contactId: string;
    allowLinks: boolean;
    autoFocus: boolean;
    ref: RefObject<Editor | null>;
  }) => ReactNode;

  renderBodyEditor?: (props: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    documents: EditorDocument[];
    atVariables: Record<string, string>;
    contactId: string;
    allowFormatting: boolean;
    allowLinks: boolean;
    autoFocus: boolean;
    onFocus?: () => void;
    ref: RefObject<Editor | null>;
  }) => ReactNode;

  renderSuggestTemplates?: (props: {
    value: string;
    atVariables: Record<string, string>;
    documents: EditorDocument[];
    onClick: (body2: string, subject?: string) => void;
    forceShow: boolean;
  }) => ReactNode;

  renderReminderInput?: (props: {
    value: string;
    onChange: (value: string) => void;
    teamId: string;
  }) => ReactNode;

  renderSelectDropdown?: (props: {
    options: EmailOption[] | ThreadOption[];
    value?: string;
    onChange: (option: { value: string; label: string }) => void;
    placeholder: string;
    leftTitle?: string;
    children: ReactNode;
    emptyContent?: ReactNode;
    size?: 'small' | 'large';
    optionRow?: (option: EmailOption | ThreadOption) => ReactNode;
  }) => ReactNode;

  renderCharCounter?: (props: {
    body2: string;
  }) => ReactNode;

  // Styles
  wrapperClassName?: string;
  contentClassName?: string;
};

// ============================================================================
// Helper Functions
// ============================================================================

function isNotEmpty(value: string | null | undefined): boolean {
  return value != null && value.trim() !== '';
}

function isNotEmptyTipTap(value: string | null | undefined): boolean {
  if (!value) return false;
  const stripped = value.replace(/<[^>]*>/g, '').trim();
  return stripped.length > 0;
}

/**
 * Convert atVariables Record to TipTapVariable array for TipTapInput
 */
function convertAtVariablesToTipTap(atVariables: Record<string, string>): TipTapVariable[] {
  return Object.entries(atVariables).map(([id, label]) => ({
    id,
    label: label || `@${id}`,
    group: USER_AT_VARS_SET.has(id) || id === 'Today' ? 'user' : 'contact',
  }));
}

/**
 * Convert EditorDocument array to TipTapDocument array for TipTapInput
 */
function convertDocumentsToTipTap(documents: EditorDocument[]): TipTapDocument[] {
  return documents.map((doc) => ({
    id: doc.id,
    label: doc.title ?? doc.id,
    imageUrl: doc.imageUrl,
  }));
}

// ============================================================================
// Component
// ============================================================================

export function CadenceStepSectionPure({
  userTeam,
  contact,
  contactUser,
  step,
  atVariables,
  documents = [],
  fromEmailOptions = [],
  toEmailOptions = [],
  threadOptions = [],
  aiChoices = [],
  aiIndex = 0,
  aiCommand = '',
  aiLoading = false,
  stepsList = [],
  allowEditStepType = true,
  allowSaveAsTemplate = true,
  allowEmailDetails = true,
  disableLikeRecentPosts = false,
  autoGenerateAi = false,
  initiallyAnimate = false,
  sendMode,
  showSendModeDropdown = false,
  onSendModeChange,
  messageAttachment,
  texts,
  isSubmitting = false,
  isAiGenerating = false,
  isSavingSnippet = false,
  reminderActions = [],
  isLoadingReminderActions = false,
  onCreateReminderAction,
  isCreatingReminderAction = false,
  onSubmit,
  onCancel,
  onChange,
  onGenerateAi,
  onAiCommandChange,
  onAiIndexChange,
  onSaveSnippet,
  onLoadThreads,
  onToast,
  renderSubjectEditor,
  renderBodyEditor,
  renderSuggestTemplates,
  renderReminderInput,
  renderSelectDropdown,
  renderCharCounter,
  wrapperClassName,
  contentClassName,
}: CadenceStepSectionPureProps) {
  const subjectRef = useRef<Editor | null>(null);
  const bodyRef = useRef<Editor | null>(null);

  // Double-submit prevention: ref for synchronous check + local state for UI
  const isSubmittingRef = useRef(false);
  const [isLocalSubmitting, setIsLocalSubmitting] = useState(false);

  // Local state
  const [changes, setChanges] = useState<StepChanges>({});
  const [showCcInput, setShowCcInput] = useState(false);
  const [showBccInput, setShowBccInput] = useState(() => userTeam.useContactEmailAsBcc ?? false);
  const [wasTemplatePasted, setWasTemplatePasted] = useState(false);
  const [deferredStepType, setDeferredStepType] = useState<CadenceStepType | undefined>();
  const [fromEmailLabel, setFromEmailLabel] = useState<string>();

  // Derived state
  const stepType = changes.type ?? step.type;
  const stepBody = changes.body2 ?? step.body2 ?? '';
  const stepSubject = changes.subject ?? step.subject ?? '';
  const stepFrom = changes.from ?? step.from ?? '';
  const stepTo = changes.to ?? step.to ?? '';
  const stepCc = changes.cc ?? step.cc ?? '';
  const stepBcc = changes.bcc ?? step.bcc ?? '';
  const onlyOpenLink = stepType === 'inmail' && step.onlyOpenLink;
  const useEmailSignature = stepType === 'email' && (step.useEmailSignature ?? userTeam.useEmailSignatureByDefault);

  const showSubject = stepType ? ['email', 'inmail'].includes(stepType) : false;
  const showBody = stepType ? ['email', 'inmail', 'message', 'call', 'connect'].includes(stepType) : false;
  const showAiSuggest = stepType ? ['email', 'inmail', 'connect', 'message'].includes(stepType) : false;
  const isPremium = userTeam.isPremium;

  const handleChanges = useCallback(
    (data: StepChanges) => {
      setChanges((current) => ({ ...current, ...data }));
      onChange(data);
    },
    [onChange],
  );

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    // Prevent double-submit: check ref synchronously (before async state updates)
    if (isSubmittingRef.current || isSubmitting) return;
    isSubmittingRef.current = true;
    setIsLocalSubmitting(true);

    try {
      if (stepType !== 'connect' && !isNotEmptyTipTap(stepBody)) {
        onToast?.('Message is required', 'error');
        return;
      }
      if ((stepType === 'email' || stepType === 'inmail') && !isNotEmptyTipTap(stepSubject)) {
        onToast?.('Subject is required', 'error');
        return;
      }

      const params: StepData = {
        id: step.id,
        type: stepType,
        subject: stepSubject || null,
        body2: stepBody || null,
        from: stepFrom || null,
        to: stepTo || null,
        cc: stepCc || null,
        bcc: stepBcc || null,
      };

      await onSubmit(params);
    } finally {
      isSubmittingRef.current = false;
      setIsLocalSubmitting(false);
    }
  };

  const handleFocusBody = () => {
    if (stepType !== 'email') return;
    if (!isNotEmpty(stepCc)) setShowCcInput(false);
    if (!isNotEmpty(stepBcc)) setShowBccInput(false);
  };

  // Filter steps list based on contact state
  const filteredSteps = useMemo(() => {
    const connectedAt = contactUser.liConnectedAt;
    const professionalEmail = contact.primaryFields?.professionalEmail?.data;
    const phone = contact.primaryFields?.phone?.data;

    const disabledState: Partial<Record<CadenceStepType, boolean>> = {
      email: !isNotEmpty(professionalEmail),
      call: !isNotEmpty(phone),
      follow: connectedAt != null,
      connect: connectedAt != null,
      inmail: connectedAt != null,
      likeRecentPosts: disableLikeRecentPosts,
    };

    return stepsList.filter((s) => !disabledState[s.value]);
  }, [stepsList, contact, contactUser, disableLikeRecentPosts]);

  // Convert atVariables and documents for TipTapInput (used when render props not provided)
  const tipTapVariables = useMemo(
    () => convertAtVariablesToTipTap(atVariables),
    [atVariables],
  );
  const tipTapDocuments = useMemo(
    () => convertDocumentsToTipTap(documents),
    [documents],
  );

  // Cc/Bcc visibility
  useEffect(() => {
    if (!allowEmailDetails) return;
    if (stepType === 'email') {
      setShowCcInput(isNotEmpty(stepCc));
      setShowBccInput(isNotEmpty(stepBcc));
    }
  }, [stepCc, stepBcc, stepType, allowEmailDetails]);

  // Ai auto-generate on mount
  useEffect(() => {
    if (autoGenerateAi && onGenerateAi) {
      void onGenerateAi(true);
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-load email threads on mount so the "reply to thread" chevron can show only when threads exist
  useEffect(() => {
    if (stepType === 'email' && onLoadThreads && threadOptions.length === 0) {
      void onLoadThreads();
    }
    // Only run on mount / when entering email step
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepType]);

  // When a fresh AI generation result arrives, sync the selected choice into the
  // local `changes` state so the editor updates immediately. The parent owns the
  // generation mutation and updates its own copy of the body, but the editor's
  // `value` is driven by `stepBody` (derived from local `changes`), which the
  // parent can't reach. Without this, the generated message wouldn't show until
  // the user toggled a variant tab (which routes through `handleChanges`).
  const prevAiChoicesRef = useRef(aiChoices);
  useEffect(() => {
    if (aiChoices === prevAiChoicesRef.current) return;
    prevAiChoicesRef.current = aiChoices;
    const choice = aiChoices[aiIndex];
    if (!choice) return;
    handleChanges({ body2: choice.body, subject: choice.subject });
  }, [aiChoices, aiIndex, handleChanges]);

  const handleAiIndexChange = (newIndex: string) => {
    const idx = parseInt(newIndex, 10);
    if (idx === -1) {
      void onGenerateAi?.(true);
      return;
    }
    if (aiChoices.length > 0 && aiChoices[idx]) {
      handleChanges({
        body2: aiChoices[idx].body,
        subject: aiChoices[idx].subject,
      });
    }
    onAiIndexChange?.(idx);
  };

  const handleSaveSnippet = async () => {
    if (!onSaveSnippet) return;
    try {
      await onSaveSnippet();
      onToast?.('Template saved', 'success');
    } catch {
      // Parent's onSaveSnippet already handles error toasting
    }
  };

  const handleTemplateClick = (body2: string, subject?: string) => {
    handleChanges({ body2, subject });
    setWasTemplatePasted(true);
  };

  const isEditorEmpty = !stepBody || stepBody === '<p></p>' || stepBody === '<div><br></div>';
  const shouldShowSuggestions = showBody && !wasTemplatePasted && (isEditorEmpty || stepBody);

  return (
    <AnimatePresence>
      {step && stepType && (
        <motion.form
          key="section"
          initial={initiallyAnimate ? false : { y: '100%' }}
          animate={{ y: '0%' }}
          exit={{ y: '100%' }}
          transition={{ type: 'tween', duration: 0.15, ease: 'easeOut' }}
          className={cn('absolute inset-0 z-10 m-0 flex flex-col bg-white dark:bg-gray-900', wrapperClassName)}
          onSubmit={handleSubmit}>
          {/* Header */}
          <CadenceStepSectionHeaderPure
            stepType={stepType}
            filteredSteps={filteredSteps}
            allowEditStepType={allowEditStepType}
            onlyOpenLink={onlyOpenLink}
            useEmailSignature={useEmailSignature}
            deferredStepType={deferredStepType}
            onDeferredStepTypeChange={setDeferredStepType}
            onCancel={onCancel}
            onChange={handleChanges}
            renderSelectDropdown={renderSelectDropdown}
          />

          {/* AI Loading Bar */}
          {isAiGenerating && (
            <div className="h-1 bg-linear-to-r from-blue-400 via-purple-400 to-blue-400 bg-size-[200%_100%] animate-shimmer" />
          )}

          {/* AI Suggest */}
          {showAiSuggest && (
            <CadenceStepSectionAiSuggestPure
              isPremium={isPremium}
              aiCommand={aiCommand}
              aiChoices={aiChoices}
              aiIndex={aiIndex}
              aiLoading={aiLoading}
              hasBody={(stepBody?.length ?? 0) > 0}
              onCommandChange={onAiCommandChange}
              onSubmit={() => { void onGenerateAi?.(); }}
              onIndexChange={handleAiIndexChange}
            />
          )}

          {/* Content */}
          <div className={cn('flex-1 flex flex-col overflow-hidden', contentClassName)}>
            {aiLoading && (
              <div className="flex items-center justify-center h-32">
                <WaveSpinner />
              </div>
            )}

            {!aiLoading && (showSubject || showBody) && (
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Email fields */}
                {stepType === 'email' && allowEmailDetails && renderSelectDropdown && (
                  <>
                    {/* From */}
                    {renderSelectDropdown?.({
                      options: fromEmailOptions,
                      value: stepFrom,
                      onChange: (option) => {
                        setFromEmailLabel(option.label);
                        handleChanges({ from: option.value });
                      },
                      placeholder: 'From',
                      leftTitle: 'From',
                      size: 'large',
                      children: <span>{fromEmailLabel || stepFrom}</span>,
                      optionRow: (opt) => (
                        <div className="flex items-center gap-2">
                          <span>{opt.label}</span>
                          {opt.helperText && (
                            <Badge size="sm" rounded type="standard">
                              {opt.helperText}
                            </Badge>
                          )}
                        </div>
                      ),
                    })}

                    {/* To with Cc/Bcc buttons */}
                    <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700">
                      {renderSelectDropdown?.({
                        options: toEmailOptions,
                        value: stepTo,
                        onChange: (option) => handleChanges({ to: option.value }),
                        placeholder: 'To',
                        leftTitle: 'To',
                        size: 'large',
                        children: <span>{stepTo}</span>,
                        optionRow: (opt) => (
                          <div className="flex items-center gap-2">
                            <span>{opt.label}</span>
                            {opt.helperText && (
                              <Badge size="sm" rounded type="standard">
                                {opt.helperText}
                              </Badge>
                            )}
                          </div>
                        ),
                      })}
                      <div className="flex gap-1 pr-2">
                        {!showCcInput && (
                          <Button onClick={() => setShowCcInput(true)} size="sm">
                            Cc
                          </Button>
                        )}
                        {!showBccInput && (
                          <Button onClick={() => setShowBccInput(true)} size="sm">
                            Bcc
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Cc */}
                    {showCcInput && (
                      <div className="flex items-center px-3 border-b border-gray-200 dark:border-gray-700">
                        <Input
                          className="w-full"
                          icon={<span>Cc</span>}
                          value={stepCc}
                          bordered={false}
                          onChange={(e) => handleChanges({ cc: e.target.value })}
                        />
                      </div>
                    )}

                    {/* Bcc */}
                    {showBccInput && (
                      <div className="flex items-center px-3 border-b border-gray-200 dark:border-gray-700">
                        <Input
                          className="w-full"
                          icon={<span>Bcc</span>}
                          value={stepBcc}
                          bordered={false}
                          onChange={(e) => handleChanges({ bcc: e.target.value })}
                        />
                      </div>
                    )}
                  </>
                )}

                {/* Subject */}
                {showSubject && (
                  <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700">
                    {renderSubjectEditor && renderSubjectEditor({
                      value: stepSubject,
                      onChange: (v) => handleChanges({ subject: v }),
                      placeholder: 'Subject',
                      documents,
                      atVariables,
                      contactId: contact.id,
                      allowLinks: stepType === 'email',
                      autoFocus: true,
                      ref: subjectRef,
                    })}
                    {!renderSubjectEditor && (
                      <TipTapInput
                        ref={subjectRef}
                        className="flex-1"
                        editorClassName="min-h-[24px]"
                        placeholder="Subject"
                        value={stepSubject}
                        onChange={(html) => handleChanges({ subject: html })}
                        variables={tipTapVariables}
                        documents={tipTapDocuments}
                        allowLinks={stepType === 'email'}
                        autoFocus
                        size="sm"
                        bordered={false}
                      />
                    )}
                    {stepType === 'email' && threadOptions.length > 0 && (
                      <div className="relative shrink-0 pr-2">
                        <Button size="sm" tabIndex={-1} readOnly tooltip="Reply to existing thread">
                          <Arrows.ChevronDown className="size-3.5" />
                        </Button>
                        <select
                          aria-label="Reply to existing thread"
                          value=""
                          onChange={(e) => {
                            if (e.target.value) handleChanges({ subject: e.target.value });
                          }}
                          className="absolute inset-0 cursor-pointer opacity-0">
                          <option value="" disabled>
                            Reply to existing thread
                          </option>
                          {threadOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                              {opt.helperText ? ` — ${opt.helperText}` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* Body */}
                {showBody && (
                  <div className="flex-1 flex flex-col overflow-hidden">
                    {renderBodyEditor && renderBodyEditor({
                      value: stepBody,
                      onChange: (v) => handleChanges({ body2: v }),
                      placeholder: stepType === 'call' ? 'Call script' : 'Body',
                      documents,
                      atVariables,
                      contactId: contact.id,
                      allowFormatting: stepType === 'email' || stepType === 'call',
                      allowLinks: stepType === 'email',
                      autoFocus: !showSubject,
                      onFocus: handleFocusBody,
                      ref: bodyRef,
                    })}
                    {!renderBodyEditor && (
                      <TipTapInput
                        ref={bodyRef}
                        className="flex-1 overflow-auto"
                        editorClassName="h-full"
                        placeholder={stepType === 'call' ? 'Call script' : 'Body'}
                        value={stepBody}
                        onChange={(html) => handleChanges({ body2: html })}
                        variables={tipTapVariables}
                        documents={tipTapDocuments}
                        allowFormatting={stepType === 'email' || stepType === 'call'}
                        allowLinks={stepType === 'email'}
                        autoFocus={!showSubject}
                        onFocus={handleFocusBody}
                        bordered={false}
                        shape="square"
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Visit step */}
            {stepType === 'visit' && (
              <p className="p-4 text-center text-gray-500">
                Our extension will visit the lead&apos;s page
              </p>
            )}

            {/* Like posts step */}
            {stepType === 'likeRecentPosts' && (
              <div className="p-4">
                <div className="mb-1 text-xs font-medium text-slate-600 dark:text-slate-300">Posts to like</div>
                <Input
                  placeholder="Posts to like"
                  onChange={(e) => handleChanges({ postsToLike: Number(e.target.value) })}
                  type="number"
                  value={changes.postsToLike ?? 3}
                />
              </div>
            )}

            {/* Reminder step */}
            {stepType === 'reminder' && (
              <div className="p-4">
                {renderReminderInput && renderReminderInput({
                  value: stepBody,
                  onChange: (v) => handleChanges({ body2: v }),
                  teamId: userTeam.teamId,
                })}
                {!renderReminderInput && (
                  <ManualSelectPure
                    value={stepBody}
                    onChange={(v) => handleChanges({ body2: v })}
                    reminderActions={reminderActions}
                    isLoading={isLoadingReminderActions}
                    onCreateReminder={onCreateReminderAction}
                    isCreating={isCreatingReminderAction}
                    onError={(msg) => onToast?.(msg, 'error')}
                    fullWidth
                  />
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <CadenceStepSectionActionsPure
            stepType={stepType}
            stepBody={stepBody}
            stepSubject={stepSubject}
            isLoading={isSubmitting || isLocalSubmitting}
            isSavingSnippet={isSavingSnippet}
            allowSaveAsTemplate={allowSaveAsTemplate}
            wasTemplatePasted={wasTemplatePasted}
            showSendModeDropdown={showSendModeDropdown}
            sendMode={sendMode}
            texts={texts}
            onSendModeChange={onSendModeChange}
            onSaveSnippet={handleSaveSnippet}
            onShowSuggestions={() => setWasTemplatePasted(false)}
            renderSuggestTemplates={
              shouldShowSuggestions && renderSuggestTemplates
                ? () =>
                    renderSuggestTemplates({
                      value: stepBody,
                      atVariables,
                      documents,
                      onClick: handleTemplateClick,
                      forceShow: isEditorEmpty,
                    })
                : undefined
            }
            renderCharCounter={
              stepType === 'connect' && stepBody ? () => renderCharCounter?.({ body2: stepBody }) : undefined
            }
            messageAttachment={messageAttachment}
            onAttachmentError={(msg) => onToast?.(msg, 'error')}
          />
        </motion.form>
      )}

      {/* Step type change confirmation popup */}
      {deferredStepType && (
        <UpdateStepTypePopup
          onDelete={() => {
            handleChanges({ type: deferredStepType });
            setDeferredStepType(undefined);
          }}
          onClose={() => setDeferredStepType(undefined)}
          showActionCanNotBeUndone={false}
        />
      )}
    </AnimatePresence>
  );
}

// ============================================================================
// Header Sub-Component
// ============================================================================

type HeaderPureProps = {
  stepType: CadenceStepType;
  filteredSteps: StepListItem[];
  allowEditStepType?: boolean;
  onlyOpenLink?: boolean;
  useEmailSignature?: boolean;
  deferredStepType?: CadenceStepType;
  onDeferredStepTypeChange: (type: CadenceStepType | undefined) => void;
  onCancel?: () => void;
  onChange: (changes: StepChanges) => void;
  renderSelectDropdown?: CadenceStepSectionPureProps['renderSelectDropdown'];
};

function CadenceStepSectionHeaderPure({
  stepType,
  filteredSteps,
  allowEditStepType,
  onlyOpenLink,
  useEmailSignature,
  deferredStepType: _deferredStepType,
  onDeferredStepTypeChange,
  onCancel,
  onChange,
  renderSelectDropdown: _renderSelectDropdown,
}: HeaderPureProps) {
  useEscStack('CadenceStepSectionHeader', ESC_PRIORITY.MED, onCancel);

  const handleStepTypeChange = (value: CadenceStepType) => {
    if (stepType === 'email' && ['inmail', 'message', 'call', 'connect'].includes(value)) {
      onDeferredStepTypeChange(value);
    } else {
      onChange({ type: value });
    }
  };

  const currentStepLabel = filteredSteps.find((s) => s.value === stepType)?.labelFull;
  const stepTypeOptions = filteredSteps.map((s) => ({
    value: s.value,
    label: String(s.labelFull ?? s.label),
  }));

  return (
    <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-2">
        {stepType && <StepIcon className="size-5" iconType={stepType} />}
        {allowEditStepType && filteredSteps.length > 1 && (
          <FilterSelect
            value={stepType}
            options={stepTypeOptions}
            onChange={(val: string) => handleStepTypeChange(val as CadenceStepType)}
            size="sm"
            bordered={false}
            className="font-medium"
          />
        )}
        {(!allowEditStepType || filteredSteps.length <= 1) && (
          <span className="font-medium">{currentStepLabel}</span>
        )}
        {stepType === 'inmail' && (
          <Button
            size="sm"
            color={onlyOpenLink ? 'blue' : 'gray'}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange({ onlyOpenLink: !onlyOpenLink });
            }}>
            Only Open
          </Button>
        )}
        {stepType === 'email' && (
          <Button
            size="sm"
            color={useEmailSignature ? 'blue' : 'gray'}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange({ useEmailSignature: !useEmailSignature });
            }}>
            Use Email Signature
          </Button>
        )}
      </div>
      {onCancel && (
        <Button text icon={<General.XMark />} onClick={onCancel} />
      )}
    </div>
  );
}

// ============================================================================
// AI Suggest Sub-Component
// ============================================================================

type AiSuggestPureProps = {
  isPremium: boolean;
  aiCommand: string;
  aiChoices: AiChoice[];
  aiIndex: number;
  aiLoading: boolean;
  hasBody: boolean;
  onCommandChange?: (command: string) => void;
  onSubmit: () => void;
  onIndexChange: (index: string) => void;
};

function CadenceStepSectionAiSuggestPure({
  isPremium,
  aiCommand,
  aiChoices,
  aiIndex,
  aiLoading,
  hasBody,
  onCommandChange,
  onSubmit,
  onIndexChange,
}: AiSuggestPureProps) {
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 bg-linear-to-r from-indigo-50/60 to-blue-50/60 dark:from-indigo-950/40 dark:to-blue-950/40 px-3.5 py-3 space-y-2">
      {isPremium && (
        <>
          <Input
            size="sm"
            value={aiCommand}
            onChange={(e) => onCommandChange?.(e.currentTarget.value)}
            placeholder={hasBody ? 'Improve message with AI or provide custom instructions' : 'Generate message with AI or provide custom instructions'}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                onSubmit();
              }
            }}
            disabled={aiLoading}
            className="w-full"
            icon={
              aiLoading ? (
                <WaveSpinner size={17} />
              ) : (
                <Tooltip
                  label="Press Enter or click to generate. Provide custom instructions for tailored results."
                  placement="top"
                  autoPlace={false}>
                  <button
                    type="button"
                    onClick={onSubmit}
                    className="flex items-center justify-center hover:scale-110 transition-transform">
                    <General.Zap className="size-3.5 text-indigo-400 dark:text-indigo-300" />
                  </button>
                </Tooltip>
              )
            }
          />
          {aiChoices.length > 0 && (
            <div className="flex justify-end">
              <ButtonSegment size="sm" selectedId={`${aiIndex}`} onChange={onIndexChange}>
                {aiChoices.map((_, index) => (
                  <Button key={index} id={`${index}`} type="button">
                    {index + 1}
                  </Button>
                ))}
                <Button id="-1" icon={<Arrows.RefreshCw04 />} type="button" />
              </ButtonSegment>
            </div>
          )}
        </>
      )}
      {!isPremium && (
        <Button size="sm" tooltip="AI is only for premium accounts, contact Poseidon to get a premium account">
          Upgrade for AI
        </Button>
      )}
    </div>
  );
}

// ============================================================================
// Actions Sub-Component
// ============================================================================

type ActionsPureProps = {
  stepType: CadenceStepType;
  stepBody: string;
  stepSubject: string;
  isLoading: boolean;
  isSavingSnippet: boolean;
  allowSaveAsTemplate?: boolean;
  wasTemplatePasted: boolean;
  showSendModeDropdown?: boolean;
  sendMode?: SendMode;
  texts?: { submit?: string };
  onSendModeChange?: (mode: SendMode) => void;
  onSaveSnippet: () => void;
  onShowSuggestions: () => void;
  renderSuggestTemplates?: () => ReactNode;
  renderCharCounter?: () => ReactNode;
  /** File attachment affordance (message/inmail only). Omitted → no attach button. */
  messageAttachment?: MessageAttachmentState;
  onAttachmentError?: (message: string) => void;
};

function CadenceStepSectionActionsPure({
  stepType,
  stepBody,
  stepSubject,
  isLoading,
  isSavingSnippet,
  allowSaveAsTemplate,
  wasTemplatePasted,
  showSendModeDropdown,
  sendMode,
  texts,
  onSendModeChange,
  onSaveSnippet,
  onShowSuggestions,
  renderSuggestTemplates,
  renderCharCounter,
  messageAttachment,
  onAttachmentError,
}: ActionsPureProps) {
  const handleSaveSnippetAllowed = stepType ? ['message', 'email', 'inmail', 'connect'].includes(stepType) : false;
  const isSubjectNotEmpty = isNotEmptyTipTap(stepSubject);
  const isBodyNotEmpty = isNotEmptyTipTap(stepBody);
  const allowAttachment = stepType === 'message' || stepType === 'inmail';

  return (
    <div className="flex flex-col pt-0.5 px-2 pb-2">
      {renderSuggestTemplates?.()}

      <div className="flex justify-between px-2 pt-2">
        <div className="flex flex-row items-center gap-2">
          {allowSaveAsTemplate && handleSaveSnippetAllowed && (isSubjectNotEmpty || isBodyNotEmpty) && (
            <Button
              onClick={onSaveSnippet}
              disabled={isSavingSnippet}
              tooltip="Save as a message template"
              status={isSavingSnippet ? 'updating' : undefined}
              icon={<General.PlusSquare />}
              color="gray"
              size="sm"
            />
          )}
          {messageAttachment && allowAttachment && (
            <MessageAttachmentButtonPure
              filename={messageAttachment.filename}
              onSelectFile={messageAttachment.onSelectFile}
              onRemove={messageAttachment.onRemove}
              isUploading={messageAttachment.isUploading}
              disabled={isLoading}
              onError={onAttachmentError}
            />
          )}
          {renderCharCounter?.()}
        </div>

        {wasTemplatePasted && (
          <Button
            onClick={onShowSuggestions}
            tooltip="Show suggestions"
            icon={<General.Eye />}
            size="sm">
            Show suggestions
          </Button>
        )}

        {showSendModeDropdown && onSendModeChange && (
          <ButtonGroup color="blue" size="sm">
            <Button
              type="submit"
              disabled={isLoading}
              icon={isLoading ? <WaveSpinner /> : <General.CheckCircle />}>
              {sendMode === 'scheduled' ? 'Schedule to Send' : 'Send'}
            </Button>
            <Button disabled={isLoading} className="relative px-1.5">
              <select
                value={sendMode}
                onChange={(e) => onSendModeChange(e.target.value as SendMode)}
                disabled={isLoading}
                className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed">
                <option value="scheduled">Schedule to Send</option>
                <option value="now">Send Now (ignore schedule)</option>
              </select>
              <Arrows.ChevronDown className="size-4" />
            </Button>
          </ButtonGroup>
        )}

        {!showSendModeDropdown && (
          <Button
            type="submit"
            color="blue"
            size="sm"
            disabled={isLoading}
            icon={isLoading ? <WaveSpinner /> : <General.CheckCircle />}>
            {texts?.submit ?? 'Send'}
          </Button>
        )}
      </div>
    </div>
  );
}
